import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  ArrowLeft, Check, Clock, DoorOpen, X, MessageSquare, Save
} from 'lucide-react';
import { motion } from 'framer-motion';

export default function GroupDetailView({ groupName, campers, date, onBack }) {
  const { getAttendance, setAttendanceForCamper, getNotes, setNotesForCamper, campDates } = useApp();
  const [timeDialog, setTimeDialog] = useState(null);
  const [notesDialog, setNotesDialog] = useState(null);
  const [tempTime, setTempTime] = useState('');
  const [tempNotes, setTempNotes] = useState('');

  const dateInfo = campDates.find(d => d.date === date);

  const summary = campers.reduce((acc, camper) => {
    const att = getAttendance(camper.id, date);
    acc[att.status] = (acc[att.status] || 0) + 1;
    return acc;
  }, { present: 0, late: 0, leaving: 0, absent: 0, not_selected: 0 });

  const handleToggleLate = (camperId) => {
    const att = getAttendance(camperId, date);
    if (att.lateTime) {
      // Toggle off — clear late time
      setAttendanceForCamper(camperId, date, { ...att, lateTime: null, status: att.earlyTime ? 'leaving' : (att.status === 'late' ? 'not_selected' : att.status) });
    } else {
      // Auto-capture current time
      const now = new Date();
      const hours = now.getHours();
      const minutes = now.getMinutes();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const h = hours % 12 || 12;
      const m = minutes < 10 ? '0' + minutes : minutes;
      const timeStr = `${h}:${m} ${ampm}`;
      setAttendanceForCamper(camperId, date, { ...att, lateTime: timeStr, status: 'late' });
    }
  };

  const handleToggleLeaving = (camperId) => {
    const att = getAttendance(camperId, date);
    if (att.earlyTime) {
      // Toggle off — clear early time
      setAttendanceForCamper(camperId, date, { ...att, earlyTime: null, status: att.lateTime ? 'late' : (att.status === 'leaving' ? 'not_selected' : att.status) });
    } else {
      // Open dialog to manually enter pickup time
      setTimeDialog({ camperId, type: 'leaving' });
    }
  };

  const handleSaveTime = () => {
    if (!timeDialog || !tempTime) return;
    const att = getAttendance(timeDialog.camperId, date);
    setAttendanceForCamper(timeDialog.camperId, date, { ...att, earlyTime: tempTime, status: att.lateTime ? 'late' : 'leaving' });
    setTimeDialog(null);
    setTempTime('');
  };

  const handleOpenNotes = (camperId) => {
    setTempNotes(getNotes(camperId, date));
    setNotesDialog(camperId);
  };

  const handleSaveNotes = () => {
    if (notesDialog) {
      setNotesForCamper(notesDialog, date, tempNotes);
      setNotesDialog(null);
      setTempNotes('');
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-24">
      <div className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-[#E2E8F0] shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={onBack} className="rounded-full" data-testid="group-detail-back-button">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-[#0F172A] font-['Fredoka']">{groupName}</h1>
            <p className="text-sm text-[#475569]">{dateInfo?.fullDate}</p>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-4">
        <div className="space-y-3">
          {campers.map((camper, index) => (
            <CamperRow
              key={camper.id}
              camper={camper}
              date={date}
              index={index}
              onToggleLate={() => handleToggleLate(camper.id)}
              onToggleLeaving={() => handleToggleLeaving(camper.id)}
              onOpenNotes={() => handleOpenNotes(camper.id)}
            />
          ))}
        </div>

        <motion.div className="mt-6 bg-white rounded-2xl shadow-lg border border-[#E2E8F0] p-5" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <h3 className="text-sm font-bold text-[#475569] uppercase tracking-wider mb-3">Summary</h3>
          <div className="flex flex-wrap gap-3">
            {summary.not_selected > 0 && (
              <Badge className="bg-yellow-100 text-yellow-800 border border-yellow-300 px-3 py-1.5" data-testid="holiday-summary-not-selected">
                <span className="w-4 h-4 mr-1 font-bold">?</span>{summary.not_selected} Not Selected
              </Badge>
            )}
            <Badge className="status-present px-3 py-1.5"><Check className="w-4 h-4 mr-1" />{summary.present} Present</Badge>
            <Badge className="status-late px-3 py-1.5"><Clock className="w-4 h-4 mr-1" />{summary.late} Late</Badge>
            <Badge className="status-leaving px-3 py-1.5"><DoorOpen className="w-4 h-4 mr-1" />{summary.leaving} Leaving Early</Badge>
            <Badge className="status-absent px-3 py-1.5"><X className="w-4 h-4 mr-1" />{summary.absent} Absent</Badge>
            <Badge className="bg-[#0055A4] text-white px-3 py-1.5">Total: {campers.length}</Badge>
          </div>
        </motion.div>
      </div>

      {/* Early Dismissal Time Dialog */}
      <Dialog open={!!timeDialog} onOpenChange={(open) => !open && setTimeDialog(null)}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-['Fredoka']">Early Dismissal</DialogTitle>
            <DialogDescription>
              Enter the time the pickup person will arrive (from call/email)
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input value={tempTime} onChange={(e) => setTempTime(e.target.value)} placeholder="2:30 PM" className="h-12 rounded-xl" data-testid="time-input" autoFocus />
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setTimeDialog(null)} className="flex-1 rounded-xl">Cancel</Button>
            <Button onClick={handleSaveTime} disabled={!tempTime} className="flex-1 rounded-xl bg-[#0055A4] hover:bg-[#004080]" data-testid="save-time-log-button">
              <Save className="w-4 h-4 mr-2" />Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Notes Dialog */}
      <Dialog open={!!notesDialog} onOpenChange={(open) => !open && setNotesDialog(null)}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-['Fredoka']">Camper Notes</DialogTitle>
            <DialogDescription>Add notes for this camper for today</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea value={tempNotes} onChange={(e) => setTempNotes(e.target.value)} placeholder="Enter notes here..." className="min-h-[100px] rounded-xl" data-testid="notes-textarea" />
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setNotesDialog(null)} className="flex-1 rounded-xl">Cancel</Button>
            <Button onClick={handleSaveNotes} className="flex-1 rounded-xl bg-[#0055A4] hover:bg-[#004080]" data-testid="save-notes-button">
              <Save className="w-4 h-4 mr-2" />Save Notes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CamperRow({ camper, date, index, onToggleLate, onToggleLeaving, onOpenNotes }) {
  const { getAttendance, setAttendanceForCamper, getNotes } = useApp();
  const attendance = getAttendance(camper.id, date);
  const notes = getNotes(camper.id, date);
  
  const initials = `${(camper.firstName || '?')[0]}${(camper.lastName || '?')[0]}`.toUpperCase();
  const isNotSelected = attendance.status === 'not_selected';

  const handleStatusChange = (status) => {
    if (status === 'present') {
      setAttendanceForCamper(camper.id, date, { status: 'present', lateTime: null, earlyTime: null });
    } else if (status === 'absent') {
      setAttendanceForCamper(camper.id, date, { status: 'absent', lateTime: null, earlyTime: null });
    }
  };

  return (
    <motion.div
      className={`rounded-2xl shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] border p-4 ${
        isNotSelected
          ? 'bg-yellow-50 border-yellow-300'
          : 'bg-white border-[#E2E8F0]'
      }`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.03 }}
      data-testid={`camper-row-${camper.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-blue-100 text-blue-800 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-[#0F172A]">{camper.firstName} {camper.lastName}</p>
          <div className="flex flex-wrap gap-2 mt-2">
            {attendance.lateTime && (
              <Badge className="status-late text-xs px-2 py-0.5"><Clock className="w-3 h-3 mr-1" />Arrived {attendance.lateTime}</Badge>
            )}
            {attendance.earlyTime && (
              <Badge className="status-leaving text-xs px-2 py-0.5"><DoorOpen className="w-3 h-3 mr-1" />Dismissed {attendance.earlyTime}</Badge>
            )}
            {notes && (
              <Badge variant="outline" className="text-xs px-2 py-0.5"><MessageSquare className="w-3 h-3 mr-1" />Has notes</Badge>
            )}
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            <Select
              value={isNotSelected ? undefined : attendance.status}
              onValueChange={handleStatusChange}
            >
              <SelectTrigger
                className={`w-[140px] h-9 rounded-lg text-sm ${
                  isNotSelected ? 'border-yellow-400 bg-yellow-50 text-yellow-700 font-semibold' : ''
                }`}
                data-testid={`camper-status-select-${camper.id}`}
              >
                <SelectValue placeholder="-- Select --" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="present"><div className="flex items-center"><Check className="w-4 h-4 mr-2 text-green-600" />Present</div></SelectItem>
                <SelectItem value="absent"><div className="flex items-center"><X className="w-4 h-4 mr-2 text-red-600" />Absent</div></SelectItem>
              </SelectContent>
            </Select>
            <Button variant={attendance.lateTime ? 'default' : 'outline'} size="sm" onClick={onToggleLate} className={`rounded-lg h-9 ${attendance.lateTime ? 'bg-amber-500 hover:bg-amber-600' : ''}`} data-testid={`camper-late-button-${camper.id}`}>
              <Clock className="w-4 h-4 mr-1" />Late
            </Button>
            <Button variant={attendance.earlyTime ? 'default' : 'outline'} size="sm" onClick={onToggleLeaving} className={`rounded-lg h-9 ${attendance.earlyTime ? 'bg-rose-500 hover:bg-rose-600' : ''}`} data-testid={`camper-leaving-button-${camper.id}`}>
              <DoorOpen className="w-4 h-4 mr-1" />Leaving
            </Button>
            <Button variant="outline" size="sm" onClick={onOpenNotes} className="rounded-lg h-9" data-testid={`camper-notes-button-${camper.id}`}>
              <MessageSquare className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
